import { Badge } from "../../../../Components/Badge";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

export const SkeletonBox = styled.div<{ width?: string; height?: string; $borderRadius?: string }>`
  background: linear-gradient(
    90deg,
    #F3F4F6 0%,
    #E5E7EB 50%,
    #F3F4F6 100%
  );
  background-size: 468px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.$borderRadius || '0.5rem'};
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '1rem'};
`;

export const SkeletonCircle = styled(SkeletonBox)`
  border-radius: 50%;
  width: ${(props) => props.width || '3rem'};
  height: ${(props) => props.height || '3rem'};
`;

export const SubscriptionContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.large};
  padding: 1.5rem;
  background-color: #F3F4F6;
  min-height: 100%;
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const Card = styled.div`
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 0.75rem;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const CardTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
`;

export const CardDescription = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.25rem;
`;

export const PlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #DBEAFE;
  color: #1E40AF;
  margin-bottom: 0.5rem;
`;

export const IncludedInPlanTag = styled(Badge)`
  background-color: #D1FAE5;
  color: #065F46;
  font-weight: 600;
`;

export const PlanHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

export const PlanName = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
`;

export const PlanPrice = styled.div`
  text-align: right;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

export const PriceAmount = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
`;

export const PricePeriod = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  color: #6B7280;
`;

export const RenewalInfo = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.25rem;
`;

export const FeaturesSection = styled.div`
  border-top: 1px solid #E5E7EB;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
`;

export const FeatureIcon = styled.span`
  color: #22C55E;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ActionsRow = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
`;

export const PaymentMethodCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  background-color: #F9FAFB;
`;

export const PaymentIcon = styled.div`
  background-color: #FFFFFF;
  padding: 0.375rem;
  border-radius: 0.25rem;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 1.5rem;
    width: 1.5rem;
    color: #1F2937;
  }
`;

export const PaymentDetails = styled.div`
  flex: 1;
  margin-left: 0.75rem;
`;

export const PaymentBrand = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1F2937;
  margin: 0;
`;

export const PaymentNumber = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
`;

export const PaymentExpiry = styled.p`
  font-size: 0.75rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.125rem;
`;

export const EditButton = styled.button`
  font-size: 0.875rem;
  color: #3B82F6;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: #2563EB;
  }
`;

export const BillingInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const BillingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const BillingLabel = styled.span`
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const BillingValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1F2937;
`;

/** A billing field Stripe has no value for, with a link to fill it in. */
export const MissingBillingValue = styled.span`
  font-size: 0.875rem;
  color: #6B7280;

  a {
    color: #3B82F6;
    font-weight: 500;
    cursor: pointer;
  }

  a:hover {
    color: #2563EB;
  }
`;

export const InvoicesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  divide-y divide-gray-100;
`;

export const InvoiceItem = styled.li`
  padding: 0.75rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #F3F4F6;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export const InvoiceDate = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1F2937;
  margin: 0;
`;

export const InvoiceDescription = styled.p`
  font-size: 0.75rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.125rem;
`;

export const InvoiceAmount = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1F2937;
`;

export const DownloadButton = styled.button`
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &:hover {
    color: #1F2937;
  }
`;

export const ViewAllLink = styled.a`
  font-size: 0.75rem;
  color: #3B82F6;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: #2563EB;
  }
`;

export const CancelButton = styled.button`
  background-color: #FFFFFF;
  border: 1px solid #D1D5DB;
  color: #6B7280;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: #FEE2E2;
    border-color: #EF4444;
    color: #DC2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CancelWarning = styled.div`
  padding: 1rem;
  background-color: #FEF3C7;
  border: 1px solid #FBBF24;
  border-radius: 0.5rem;
  color: #92400E;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-top: 1rem;
`;

export const EmptyStateWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  gap: 1.5rem;
  background-color: #FFFFFF;
  border-radius: 0.75rem;
`;

export const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
`;

export const EmptyStateDescription = styled.p`
  font-size: 1rem;
  color: #6B7280;
  margin: 0;
  max-width: 500px;
  line-height: 1.6;
`;

export const EmptyStateActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

export const TrialBanner = styled.div<{ $variant?: "info" | "warning" }>`
  padding: 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 500px;

  ${(props) => {
    if (props.$variant === "warning") {
      return `
        background-color: #FEF3C7;
        border: 1px solid #FBB024;
        color: #92400E;
      `;
    }
    return `
      background-color: #DBEAFE;
      border: 1px solid #60A5FA;
      color: #1E40AF;
    `;
  }}
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** A PlanBadge pinned to the top-left corner of an EmptyStateWrapper, where a Card would have it. */
export const CornerPlanBadge = styled(PlanBadge)`
  position: absolute;
  top: 1.25rem;
  left: 1.25rem;
  margin-bottom: 0;
`;

export const CreditsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

export const CreditBalance = styled.p`
  font-size: 2.25rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  line-height: 1;
`;

export const CreditBalanceLabel = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  margin-top: 0.5rem;
`;

/** A card whose last item sinks to the bottom, level with the last line of the card beside it. */
export const CreditsCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

/** As tall as a FeatureItem row (its icon), so its text lines up with the plan's last feature. */
export const BuyCreditsLink = styled(ViewAllLink)`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: 1.125rem;
  margin-top: auto;
  padding-top: 0.5rem;
`;

export const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3B82F6;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #2563EB;
  }
`;

export const TransactionAmount = styled.span<{ $positive: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => (props.$positive ? "#15803D" : "#1F2937")};
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;
